import React, { useEffect, useRef, useState } from "react";
import { TIMEOUTS, api, setAuthToken } from "@/lib/api";
import QedaraLogo from "@/components/QedaraLogo";
import { roleLabel } from "@/lib/labels";
import { Loader2, ArrowRight, AlertTriangle, Upload, Check, SkipForward } from "lucide-react";

const STEPS = ["Cont", "Aspect", "Echipă", "Gata"];
const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function Progress({ step }) {
  return (
    <ol className="flex items-center gap-1.5 mb-5" aria-label="Progres configurare">
      {STEPS.map((label, i) => (
        <li key={label} className="flex-1">
          <div className={`h-1 ${i <= step ? "bg-brand" : "bg-surface-2"}`} />
          <span className={`label-tiny mt-1 block ${i <= step ? "text-brand" : "text-fg-muted"}`}>
            {label}
          </span>
        </li>
      ))}
    </ol>
  );
}

export default function SetupWorkspace({ token }) {
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const [slug, setSlug] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [workspace, setWorkspace] = useState(null);

  const [color, setColor] = useState("#176CFF");
  const [uploading, setUploading] = useState(false);
  const [logoDone, setLogoDone] = useState(false);
  const fileRef = useRef(null);

  const [emails, setEmails] = useState("");
  const [role, setRole] = useState("member");
  const [invited, setInvited] = useState(0);

  useEffect(() => {
    if (!token) { setError("Link de configurare invalid."); return; }
    api.get("/setup", { params: { token } })
      .then((r) => { setInvite(r.data); setSlug(r.data.suggested_slug || ""); })
      .catch((e) => setError(e?.response?.data?.detail || "Invitaţie invalidă."));
  }, [token]);

  const mismatch = confirm.length > 0 && password !== confirm;
  const ready = password.length >= 8 && !mismatch && slug.length >= 3 && !busy;

  const createWorkspace = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/setup/complete", { token, password, workspace_slug: slug });
      setAuthToken(data.token);                       // steps 2 and 3 need to be authenticated
      localStorage.setItem("vs_workspace", data.workspace.id);
      setWorkspace(data.workspace);
      setStep(1);
    } catch (err) {
      setError(err?.response?.data?.detail || "Configurarea a eşuat.");
    } finally { setBusy(false); }
  };

  const saveBranding = async () => {
    if (!HEX.test(color)) { setStep(2); return; }
    setBusy(true);
    try {
      await api.patch(`/workspaces/${workspace.id}/branding`, { brand_primary_color: color });
    } catch { /* personalisation is optional — never block the flow on it */ }
    finally { setBusy(false); setStep(2); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/workspaces/${workspace.id}/branding/logo`, fd, { timeout: TIMEOUTS.upload });
      setLogoDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Logo-ul nu a putut fi încărcat.");
    } finally { setUploading(false); }
  };

  const sendInvites = async () => {
    const list = emails.split(",").map((x) => x.trim()).filter((x) => x.includes("@"));
    setBusy(true);
    let ok = 0;
    for (const email of list) {
      try {
        await api.post(`/workspaces/${workspace.id}/invites`, { email, role });
        ok += 1;
      } catch { /* one bad address must not sink the rest */ }
    }
    setInvited(ok);
    setBusy(false);
    setStep(3);
  };

  if (error && !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg text-fg p-6">
        <div className="max-w-md text-center space-y-3">
          <AlertTriangle className="w-8 h-8 text-destructive mx-auto" />
          <p className="text-sm">{error}</p>
          <a href="/" className="text-xs text-brand-2 hover:text-brand">Înapoi la Qedara Chat</a>
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg text-fg">
        <div className="flex items-center gap-3 text-sm label-tiny">
          <div className="w-2 h-2 bg-brand pulse-amber" /> se verifică invitaţia…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg text-fg p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <QedaraLogo variant="icon" markSize={32} />
          <div className="min-w-0">
            <h1 className="font-bold tracking-tight truncate">
              {step === 0 ? `Bun venit, ${invite.owner_name}!` : invite.workspace_name}
            </h1>
            <p className="text-xs text-fg-muted truncate">
              {step === 0 ? `Configurează workspace-ul ${invite.workspace_name}.` : STEPS[step]}
            </p>
          </div>
        </div>

        <Progress step={step} />

        {step === 0 && (
          <form onSubmit={createWorkspace} className="bg-surface border border-line/10 p-5 space-y-4">
            <div>
              <label htmlFor="setup-email" className="label-tiny block mb-1.5">Email</label>
              <input id="setup-email" value={invite.email} readOnly className="input-flat mono w-full opacity-60" />
            </div>
            <div>
              <label htmlFor="setup-slug" className="label-tiny block mb-1.5">Adresa workspace-ului</label>
              <div className="flex items-center gap-1.5">
                <input
                  id="setup-slug" data-testid="setup-slug" value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                  maxLength={30} className="input-flat mono flex-1"
                />
                <span className="text-xs text-fg-muted mono">.chat.qedara-consulting.uk</span>
              </div>
              <p className="text-[11px] text-fg-muted mt-1">3-30 caractere, litere mici, cifre şi cratime.</p>
            </div>
            <div>
              <label htmlFor="setup-password" className="label-tiny block mb-1.5">Parolă</label>
              <input id="setup-password" data-testid="setup-password" type="password" value={password}
                     onChange={(e) => setPassword(e.target.value)} className="input-flat w-full"
                     placeholder="minimum 8 caractere" />
            </div>
            <div>
              <label htmlFor="setup-confirm" className="label-tiny block mb-1.5">Confirmă parola</label>
              <input id="setup-confirm" data-testid="setup-confirm" type="password" value={confirm}
                     onChange={(e) => setConfirm(e.target.value)} className="input-flat w-full" />
              {mismatch && <p className="text-[11px] text-destructive mt-1">Parolele nu coincid.</p>}
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
            <button type="submit" data-testid="setup-submit" disabled={!ready}
                    className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {busy ? "Se creează…" : "Creează workspace-ul"}
            </button>
          </form>
        )}

        {step === 1 && (
          <div className="bg-surface border border-line/10 p-5 space-y-4" data-testid="setup-step-brand">
            <p className="text-xs text-fg-muted">Opţional — le poţi schimba oricând din Settings.</p>
            <div>
              <span className="label-tiny block mb-1.5">Logo</span>
              <button type="button" data-testid="setup-logo" onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="btn-ghost text-sm flex items-center gap-1.5 disabled:opacity-50">
                {logoDone ? <Check className="w-3.5 h-3.5 text-brand" /> : <Upload className="w-3.5 h-3.5" />}
                {uploading ? "Se încarcă…" : logoDone ? "Logo încărcat" : "Încarcă logo"}
              </button>
              <input ref={fileRef} type="file" className="hidden"
                     accept="image/jpeg,image/png,image/svg+xml,image/webp" onChange={uploadLogo} />
            </div>
            <div>
              <label htmlFor="setup-color" className="label-tiny block mb-1.5">Culoare principală</label>
              <div className="flex items-center gap-2">
                <input id="setup-color" type="color" value={HEX.test(color) ? color : "#176CFF"}
                       onChange={(e) => setColor(e.target.value)}
                       className="w-9 h-9 bg-transparent border border-line/20 p-0.5 cursor-pointer" />
                <input value={color} onChange={(e) => setColor(e.target.value)}
                       aria-label="Culoare principală, hex" className="input-flat mono flex-1" />
              </div>
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
            <div className="flex gap-2">
              <button type="button" data-testid="setup-brand-next" onClick={saveBranding} disabled={busy}
                      className="btn-primary text-sm flex-1 disabled:opacity-50">Continuă</button>
              <button type="button" onClick={() => setStep(2)}
                      className="btn-ghost text-sm flex items-center gap-1.5">
                <SkipForward className="w-3.5 h-3.5" /> Sari
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="bg-surface border border-line/10 p-5 space-y-4" data-testid="setup-step-invite">
            <div>
              <label htmlFor="setup-emails" className="label-tiny block mb-1.5">Emailuri, separate prin virgulă</label>
              <textarea id="setup-emails" data-testid="setup-emails" rows={3} value={emails}
                        onChange={(e) => setEmails(e.target.value)}
                        placeholder="ana@firma.ro, mihai@firma.ro"
                        className="input-flat mono w-full text-sm" />
            </div>
            <div>
              <label htmlFor="setup-role" className="label-tiny block mb-1.5">Rol</label>
              <select id="setup-role" data-testid="setup-role" value={role}
                      onChange={(e) => setRole(e.target.value)} className="input-flat text-sm w-full">
                <option value="member">{roleLabel("member")}</option>
                <option value="guest">{roleLabel("guest")}</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button type="button" data-testid="setup-invite-next" onClick={sendInvites} disabled={busy}
                      className="btn-primary text-sm flex-1 disabled:opacity-50">
                {busy ? "Se trimit…" : "Trimite invitaţiile"}
              </button>
              <button type="button" onClick={() => setStep(3)}
                      className="btn-ghost text-sm flex items-center gap-1.5">
                <SkipForward className="w-3.5 h-3.5" /> Sari
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="bg-surface border border-line/10 p-5 space-y-4 text-center" data-testid="setup-done">
            <Check className="w-8 h-8 text-brand mx-auto" />
            <div>
              <p className="font-bold tracking-tight">Workspace-ul tău e gata!</p>
              <p className="text-xs text-fg-muted mt-1">
                {invited > 0 ? `${invited} invitaţii trimise. ` : ""}
                Atena te așteaptă în #general.
              </p>
            </div>
            <a href="/" data-testid="setup-open" className="btn-primary w-full inline-flex items-center justify-center gap-2">
              Deschide {invite.workspace_name} <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
